import { postWithToken } from 'hooks/http'

export interface CreateSessionRequest {
  organization_id: string
  root_organization_id: string
  period_id?: string
  root_period_id?: string
  import_type: string // e.g., "categories-rewards", "reward-package"
  file_data?: string // Base64 encoded file data
  file_name?: string
  file_type?: string // e.g., "csv", "xlsx", "pdf"
}

export interface CreateSessionResponse {
  session_id: string
  user_id: string
  organization_id: string
  root_organization_id: string
  created_at: string
}

export interface SendMessageRequest {
  content: string
}

export interface SendMessageResponse {
  event_id: string
  session_id: string
  status: string
}

/**
 * Creates a new agent session and optionally uploads a file as an artifact
 */
export async function createAgentSession(token: string, request: CreateSessionRequest): Promise<CreateSessionResponse> {
  return postWithToken('agent-sessions/sessions', token, request, null)
}

/**
 * Sends a message to an existing agent session
 */
export async function sendAgentMessage(
  token: string,
  sessionId: string,
  content: string
): Promise<SendMessageResponse> {
  return postWithToken(`agent-sessions/sessions/${sessionId}/messages`, token, { content }, null)
}

/**
 * Converts a File object to base64 string
 */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => {
      const result = reader.result as string
      // Remove the data URL prefix (e.g., "data:text/csv;base64,")
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = (error) => reject(error)
  })
}

/**
 * Gets file type extension from File object
 */
export function getFileTypeExtension(file: File): string {
  const mimeToExt: Record<string, string> = {
    'text/csv': 'csv',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/pdf': 'pdf',
    'text/plain': 'txt',
    'application/json': 'json',
    'application/xml': 'xml',
  }

  return mimeToExt[file.type] || 'unknown'
}
